/**
 * The clock and the listeners behind `model/heldResponse` — and the only place on this screen that
 * writes.
 *
 * Every decision is held for `HOLD_MS` before it is sent, so `Z` can take it back without an
 * endpoint that can. What releases it early: the next decision, the screen unmounting, and
 * `pagehide`. Nothing else, and none of them may be skipped — a held write that is never released
 * is work the trainer believes they did.
 *
 * ── `pagehide`, not `beforeunload` ───────────────────────────────────────────────────────────
 * A tab going to the back/forward cache, a closed window and a navigation away all fire `pagehide`;
 * `beforeunload` is not fired on a bfcache entry at all on mobile Safari and is throttled
 * elsewhere. The write is an ordinary `fetch` and the browser may still cut it off, which is why
 * the hold is five seconds rather than thirty.
 *
 * ── A bulk act is N single idempotent writes, and ONE invalidation ───────────────────────────
 * `POST /attention-queue/responses` has no batch form and no rate limit; `GET /attention-queue` is
 * limited to 30 a minute. The responder's own `onSettled` invalidates per write, and six writes
 * settle at six different moments — **measured at six reads for six writes** — so a batch goes
 * through `respondMany`, which suppresses the per-write invalidation and reads the queue ONCE when
 * they have all settled. Repeating a response has no additional effect (R6), so a retry is harmless.
 *
 * ── The held decision lives in a REF, and the state beside it is only for rendering ──────────
 * A `setState` updater must be pure, and every transition here has a side effect — the write. So
 * the transition is computed against the ref in the handler, the write goes out, and the state is
 * set from the answer. Reading and writing a ref inside a handler or an effect is what the React
 * Compiler allows; doing it during render is what it does not.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { useAttentionResponse, type AttentionResponseCommand } from '~/features/attention-queue';

import {
  cancelHeld,
  holdDecision,
  releaseHeld,
  HOLD_MS,
  NOTHING_HELD,
  type HeldDecision,
  type HeldState,
} from '../model/heldResponse';
import type { HandledKind } from '../model/taskDeck';

/** What the screen hands in when the trainer decides something. */
export interface DecisionRequest {
  readonly kind: HandledKind;
  readonly snoozeUntilIso: string | null;
  /** One command per card, already built by the screen — it knows the reason and the person. */
  readonly commands: readonly AttentionResponseCommand[];
}

export interface HeldResponses {
  /** Take a decision. The previous one, if any, is written first. */
  readonly decide: (request: DecisionRequest) => void;
  /** Cancel what is held. Returns the decision it cancelled, or `null` when there was nothing. */
  readonly undo: () => HeldDecision | null;
  /** What is waiting, for the "Cofnij" control and the `Z` legend. */
  readonly held: HeldDecision | null;
}

export interface HeldResponsesDeps {
  /** A write came back refused: that card is the trainer's again. */
  readonly onRefused: (keys: readonly string[]) => void;
  readonly announce: (message: string) => void;
}

export function useHeldResponses({ onRefused, announce }: HeldResponsesDeps): HeldResponses {
  const responder = useAttentionResponse({ announce });
  const [held, setHeld] = useState<HeldDecision | null>(null);

  const stateRef = useRef<HeldState>(NOTHING_HELD);
  /** The commands behind each held decision. Nothing rendered reads them. */
  const pending = useRef(new Map<HeldDecision, readonly AttentionResponseCommand[]>());
  /* The responder, held so that `release` is STABLE.
   *
   * It matters: the `pagehide` effect's cleanup flushes, so an effect that re-subscribed on every
   * render would write the held decision on every render — which is the opposite of holding it.
   * The ref is written in an effect, never during render. */
  const write = useRef(responder);
  useEffect(() => {
    write.current = responder;
  });
  const refused = useRef(onRefused);
  useEffect(() => {
    refused.current = onRefused;
  });

  const send = useCallback((decision: HeldDecision | null) => {
    if (decision === null) return;
    const commands = pending.current.get(decision) ?? [];
    pending.current.delete(decision);
    if (commands.length === 0) return;
    // ONE write is one write; N of them are ONE ACT, and the difference is the read budget —
    // `GET /attention-queue` is limited to 30 a minute and the per-write invalidation would spend
    // one of those per row. See `respondMany`.
    if (commands.length === 1 && commands[0] !== undefined) {
      write.current.respond(commands[0]);
      return;
    }
    write.current.respondMany(commands, (keys) => {
      refused.current(keys);
    });
  }, []);

  /** Write whatever is held, now. The timer, the unmount and `pagehide` all land here. */
  const release = useCallback(() => {
    const step = releaseHeld(stateRef.current);
    if (step.write === null) return;
    stateRef.current = step.state;
    send(step.write);
    setHeld(null);
  }, [send]);

  const decide = useCallback(
    (request: DecisionRequest) => {
      const decision: HeldDecision = {
        kind: request.kind,
        keys: request.commands.map((command) => command.key),
        snoozeUntilIso: request.snoozeUntilIso,
      };
      pending.current.set(decision, request.commands);
      const step = holdDecision(stateRef.current, decision);
      stateRef.current = step.state;
      send(step.write);
      setHeld(decision);
    },
    [send],
  );

  const undo = useCallback((): HeldDecision | null => {
    const step = cancelHeld(stateRef.current);
    if (step.cancelled === null) return null;
    pending.current.delete(step.cancelled);
    stateRef.current = step.state;
    setHeld(null);
    return step.cancelled;
  }, []);

  // One timer per held decision, cleared the moment the decision changes — which makes "five
  // seconds passed" and "the next decision arrived" the same code path.
  useEffect(() => {
    if (held === null) return;
    const timer = setTimeout(release, HOLD_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [held, release]);

  // Leaving flushes. The listener and the cleanup are the same act for the same reason: the
  // trainer's five seconds end when their chance to use them does.
  useEffect(() => {
    window.addEventListener('pagehide', release);
    return () => {
      window.removeEventListener('pagehide', release);
      release();
    };
  }, [release]);

  // A refused SINGLE write puts its card back. `outcome` is a fresh object per settlement and names
  // the row it was about, so this cannot act on a previous write's refusal. A batch reports its own
  // refusals through `respondMany`, because `outcome` holds one row and the last settlement would
  // be the only one a caller could see.
  const outcome = responder.outcome;
  useEffect(() => {
    if (outcome !== null && !outcome.ok) onRefused([outcome.key]);
  }, [outcome, onRefused]);

  return { decide, undo, held };
}
